import {compact, isNumber, isString, isUndefined} from 'lodash';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {TagTemplate} from '../../../components/common/dropdowns/tags/tagEditorBody';
import {ParentTagOverridesEnum} from '../../../helpers/visualCustomViewHelpers';
import {HighlightTypesEnum} from '../../../models/api/highlightBaseModel';
import {ApiPatchTeamPreferences} from '../../../models/api/patchTeamPreferencesModel';
import {ApiTag, serializeTag, TagModel} from '../../../models/api/tagModel';
import {patchApiAsync, patchAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {findTagQueue} from '../../../services/tasks/specializedTaskQueues';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {showPinTagErrorsBanner} from '../../panels/inboxes/inboxesPanelActions';
import {getInboxesPanelId} from '../../panels/panelsSelectors';
import {getCompany} from '../company/companySelectors';
import {
  toggleHideTagAction,
  updateShouldShowNewTagInWorkspacesAction,
} from '../preferences/preferencesActions';
import {
  WorkspaceDropNestTagArgs,
  WorkspaceDropNestTagTargetRelationshipsEnum,
} from '../workspace/reorder/types';
import {TagModelMap} from '../workspace/visualWorkspace/visualWorkspaceHelpers';
import {showWorkspaceTagAsyncAction} from '../workspace/workspaceActions';
import {getTags} from './tagsSelectors';

export class TagNameExistsError extends CustomError {}
export class TagNameForbiddenError extends CustomError {}
export class TagUnnestNameForbiddenError extends CustomError {}
export class TagConflictWithArchivedError extends CustomError {}
export class TagNotAvailableInInboxes extends CustomError {}
export class TagHasTooManyChildren extends CustomError {}

function saveTagTemplateAction(template: TagTemplate): ThunkAsyncAction<TagModel> {
  return async (dispatch, getState) => {
    try {
      // Perform the request.
      const state = getState();
      const company = getCompany(state);
      const newParentTag = template.parentTags[0];

      const url = `${company.url}/tags`;
      const body = {
        name: template.name,
        namespace: template.namespace,
        parent_tag_id: newParentTag && newParentTag.id,
        highlight: template.highlightEmoji || template.highlightColor,
        is_visible_in_p2: template.showInCells,
        all_inboxes: template.isVisibleInAllInboxes,
        inbox_ids: template.inboxIds,
        is_sla_applies_tag: template.isSlaAppliesTag,
        type: template.type,
      };

      const tag = await postApiAsync(ApiTag, url, body);

      // Add the tag to the store.
      const queue = findTagQueue(dispatch, getState, tag.id);
      await queue.waitForTurnAsync();
      const finalTag = await queue.provideResultAsync({tags: [tag, ...template.parentTags]});
      if (!finalTag) {
        throw new Error('This should not happen.');
      }

      return finalTag;
    } catch (error) {
      if (!(error instanceof HttpResponseError)) {
        throw error;
      }

      switch (error.status) {
        case 400:
          if (!template.isVisibleInAllInboxes && (template.inboxIds || []).length === 0) {
            throw new TagNotAvailableInInboxes();
          }

          throw error;
        case 403:
          if (error.code === 'too_many_child_tags') {
            throw new TagHasTooManyChildren();
          }

          throw new TagNameForbiddenError();
        case 409:
          if (
            hasProperty(error.body, 'tag') &&
            hasProperty(error.body.tag, 'archived_at') &&
            hasProperty(error.body.tag, 'name') &&
            isString(error.body.tag.name) &&
            isNumber(error.body.tag.archived_at)
          ) {
            throw new TagConflictWithArchivedError();
          }

          throw new TagNameExistsError();
        default:
          throw error;
      }
    }
  };
}

export interface SaveTagOptions {
  newName?: string;
  newType?: HighlightTypesEnum;
  newParentTags?: ReadonlyArray<TagModel>;
  newShowInCells?: boolean;
  newHighlightEmoji?: string;
  newHighlightColor?: PaletteColorsEnum;
  newInboxIds?: ReadonlyArray<number>;
  newIsVisibleInAllInboxes?: boolean;
}

interface UpdateParentTagActionParams {
  childTagId: TagModel['id'];
  newParentTagId: TagModel['id'] | ParentTagOverridesEnum.MOVED_TO_ROOT;
  additionalTagsMap: TagModelMap;
}

export function updateParentTagAction({
  childTagId,
  newParentTagId,
  additionalTagsMap,
}: UpdateParentTagActionParams): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const tagsFromState = getTags(getState());
    const findTag = (id: TagModel['id']): TagModel | undefined => tagsFromState[id] ?? additionalTagsMap[id];

    const newParentTag =
      newParentTagId !== ParentTagOverridesEnum.MOVED_TO_ROOT ? findTag(newParentTagId) : undefined;

    await dispatch(
      saveTagAction(childTagId, {
        newParentTags: compact([newParentTag]),
      }),
    );
  };
}

export function saveTagAction(tagId: number, options: SaveTagOptions): ThunkAsyncAction<TagModel> {
  return async (dispatch, getState) => {
    // Find the tag to update.
    const queue = findTagQueue(dispatch, getState, tagId);
    const tag = queue.get();
    if (!tag) {
      throw new Error('The tag to save could not be found.');
    }

    // Update the state.
    const newTag = mergeTag(tag, options);

    const optimisticUpdateTag = computeOptimisticUpdateTag(tag, newTag);

    queue.update({tags: [optimisticUpdateTag, ...(options.newParentTags || [])]});

    await queue.waitForTurnAsync();

    try {
      const body = serializeTag(newTag);
      const serverTag = await putApiAsync(ApiTag, newTag.url, body);

      const finalTag = await queue.provideResultAsync({tags: [serverTag]});
      if (!finalTag) {
        throw new Error('This should not happen.');
      }

      return finalTag;
    } catch (error) {
      queue.reportError();

      if (!(error instanceof HttpResponseError)) {
        throw error;
      }

      switch (error.status) {
        case 400:
          if (!newTag.isVisibleInAllInboxes && (newTag.inboxIds || []).length === 0) {
            throw new TagNotAvailableInInboxes();
          }

          throw error;
        case 403:
          // If the updated tag has a new parent tag, its nesting has changed.
          if (tag.parentTagId !== newTag.parentTagId) {
            throw new TagUnnestNameForbiddenError();
          }

          throw new TagNameForbiddenError();
        case 409:
          if (
            hasProperty(error.body, 'tag') &&
            hasProperty(error.body.tag, 'archived_at') &&
            hasProperty(error.body.tag, 'name') &&
            isString(error.body.tag.name) &&
            isNumber(error.body.tag.archived_at)
          ) {
            throw new TagConflictWithArchivedError();
          }

          throw new TagNameExistsError();
        default:
          throw error;
      }
    }
  };
}

function mergeTag(tag: TagModel, options: SaveTagOptions): TagModel {
  const newParentTagId = options.newParentTags ? options.newParentTags[0]?.id : tag.parentTagId;

  const baseTag = {
    ...tag,
    parentTagId: newParentTagId,
    previousParentTagId: undefined,
    name: options.newName ?? tag.name,
    isMainInboxTag: false,
    isSystemTag: false,
    showInCells: options.newShowInCells ?? tag.showInCells,
    isVisibleInAllInboxes: options.newIsVisibleInAllInboxes ?? tag.isVisibleInAllInboxes,
    inboxIds: options.newInboxIds ?? tag.inboxIds,
  };

  if (!isUndefined(options.newType)) {
    if (options.newType === HighlightTypesEnum.EMOJI && options.newHighlightEmoji) {
      return {
        ...baseTag,
        type: HighlightTypesEnum.EMOJI,
        highlightEmoji: options.newHighlightEmoji,
      };
    }

    if (options.newType === HighlightTypesEnum.COLOR && options.newHighlightColor) {
      return {
        ...baseTag,
        type: HighlightTypesEnum.COLOR,
        highlightColor: options.newHighlightColor,
      };
    }

    return {
      ...baseTag,
      type: HighlightTypesEnum.NONE,
    };
  }

  return baseTag;
}

// Create a new tag, pin to teammate workspaces, and update the start guide if needed.
export function createTagAction(
  template: TagTemplate,
  trackingCallback?: HandlerOf<TagModel>,
): ThunkAsyncAction<TagModel> {
  return async (dispatch) => {
    const newTag = await dispatch(saveTagTemplateAction(template));

    if (template.showInWorkspaces) {
      dispatch(showWorkspaceTagAsyncAction(newTag.id, newTag.isView, newTag.namespace));
    }

    dispatch(toggleHideTagAction(newTag.id, [newTag], {value: template.showInWorkspaces}));

    if (template.teammatesToPin.length) {
      dispatch(pinTagToTeammateWorkspacesAction(template.teammatesToPin, newTag.id));
    } else {
      dispatch(updateShouldShowNewTagInWorkspacesAction(template.showInWorkspaces));
    }

    if (trackingCallback) {
      trackingCallback(newTag);
    }

    return newTag;
  };
}

function computeOptimisticUpdateTag(tag: TagModel, newTag: TagModel): TagModel {
  if (!tag.parentTagId) {
    return newTag;
  }

  // We only assign a previousParentTagId in the optimistic update if parentTagId has changed.
  // This is needed for the exception handling in TagEditorDropdown. If previousParentTagId is
  // defined, then a hidden WorkspaceCustomViewItem will render in P1.
  if (newTag.parentTagId === tag.parentTagId) {
    return newTag;
  }

  return {...newTag, previousParentTagId: tag.parentTagId};
}

export function pinTagToTeammateWorkspacesAction(
  teammateIds: ReadonlyArray<number>,
  tagId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {company, userId} = getState().data;

    const inboxesPanelId = getInboxesPanelId(getState());
    if (!inboxesPanelId) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.PIN_TAG_TO_TEAMMATE_WORKSPACES, {
      user_id: userId,
      tag_id: tagId,
      teammate_ids: teammateIds,
    });

    const preferencesUrl = `${company.url}/team/preferences`;
    const preferencesBody = {
      teammate_ids: teammateIds,
      preferences: [{name: 'personalWorkspaceTagIds', add: [tagId]}],
    };
    const preferencesRes = await patchApiAsync(ApiPatchTeamPreferences, preferencesUrl, preferencesBody);

    const workspacesUrl = `${company.url}/team/workspaces`;
    const workspacesBody = {
      teammate_ids: teammateIds,
      workspace: {
        my_inbox: {
          add: {
            inbox_folders: {items: []},
            tags: {items: [tagId]},
          },
          remove: {
            inbox_folders: {items: []},
            tags: {items: []},
          },
        },
      },
    };
    const workspacesRes = await patchAsync(workspacesUrl, workspacesBody);

    const preferencesHadFailures =
      preferencesRes.failedTeammateIdsWithError && preferencesRes.failedTeammateIdsWithError.length;
    const workspacesHadFailures =
      workspacesRes.failed_teammate_ids && workspacesRes.failed_teammate_ids.length;
    if (!preferencesHadFailures && !workspacesHadFailures) {
      return;
    }

    const preferencesErrors = preferencesRes.failedTeammateIdsWithError ?? [];
    const totalFailedTeammateIdsWithErrors = [
      ...preferencesErrors,
      ...workspacesRes.failed_teammate_ids.map((id: number) => ({
        teammateId: id,
        errorCode: '',
      })),
    ];
    dispatch(showPinTagErrorsBanner(inboxesPanelId, totalFailedTeammateIdsWithErrors));
  };
}

export function nestSingleTagInsideNewParentAction({
  childTagId,
  targetTagId,
  targetRelationship,
}: WorkspaceDropNestTagArgs): ThunkAction {
  return async (dispatch, getState) => {
    const tags = getTags(getState());
    const childTag = tags[childTagId];
    if (!childTag) {
      throw new Error('The tag to nest could not be found.');
    }

    const targetTag = (targetTagId && tags[targetTagId]) || undefined;

    const newParentTag =
      targetRelationship === WorkspaceDropNestTagTargetRelationshipsEnum.SIBLING && targetTag?.parentTagId
        ? tags[targetTag.parentTagId]
        : targetTag;

    if (newParentTag === childTag.parentTagId) {
      return undefined;
    }

    return dispatch(
      saveTagAction(childTagId, {
        newParentTags: compact([newParentTag]),
      }),
    );
  };
}
